import { memo } from "react";

import { type Maybe } from "@shopify/hydrogen-react/storefront-api-types";

import { Badge, type BadgeType } from "@components/Badge";
import { Image } from "@components/Image";
import { type ProductHeroSettings } from "@root/tablescape/state";
import { ProductVideoModalButton } from "@sections/ProductHero/ProductVideoModalButton";
import { tw, type TTailwindString } from "classnames";

type Props = {
  badgeImage?: { alt: string; src: string };
  badgeText: Maybe<string>;
  badgeType: Maybe<BadgeType>;
  lazyLoadImages: boolean;
  lcpMedia: ProductHeroSettings["productGalleryCarousel"]["lcpMedia"];
};

const styles = {
  wrapper: tw(
    "bg-background-secondary",
    "relative",
    "col-span-2",
    "flex",
    "w-full",
    "items-center",
    "md:aspect-[764/577]" as TTailwindString
  ),
  image: tw("object-cover", "object-center-60", "h-full"),
  badge: tw("absolute", "top-8", "left-8"),
  badgeImage: tw("absolute", "right-8", "top-8"),
  videoModalButton: tw("absolute", "z-30", "md:bottom-8", "md:left-8"),
};

const ProductGalleryGridHeroImageBase = ({
  badgeImage,
  badgeText,
  badgeType,
  lazyLoadImages,
  lcpMedia,
}: Props) => {
  return (
    <div className={styles.wrapper}>
      <img
        alt={lcpMedia.altText}
        className={styles.image}
        decoding="async"
        // eslint-disable-next-line react/no-unknown-property
        fetchpriority="high"
        height="767"
        loading="eager"
        src={lcpMedia.sources[0].src}
        srcSet={lcpMedia.sources
          .map(({ devicePixelRatio, src }) => `${src} ${devicePixelRatio}x`)
          .join(", ")}
        width="767"
      />
      {badgeText && (
        <div className={styles.badge}>
          <Badge size="lg" type={badgeType ?? undefined}>
            {badgeText}
          </Badge>
        </div>
      )}
      {badgeImage && (
        <Image
          alt={badgeImage.alt}
          aspectRatio="1/1"
          className={styles.badgeImage}
          height={80}
          loading={lazyLoadImages ? "lazy" : "eager"}
          src={badgeImage.src}
          width={80}
        />
      )}
      <div className={styles.videoModalButton}>
        <ProductVideoModalButton />
      </div>
    </div>
  );
};

export const ProductGalleryGridHeroImage = memo(
  ProductGalleryGridHeroImageBase
);
